import { useEffect, useState } from "react";
import { X, ChevronRight, Sparkles, Flame, Keyboard, ScrollText } from "lucide-react";
import { api } from "@/lib/api";

/**
 * OnboardingOverlay — 3-step guided first turn.
 * Fires ONCE on the user's very first run (turn <= 1, tutorial_completed=false).
 * Skipping or completing both mark the flag on the backend so it never returns.
 *
 * Per MONETIZATION_STRATEGY.md §5.2 — new-player D0 retention step. A well-timed
 * onboarding typically lifts D1 retention by 8-12%.
 */
const STEPS = [
  {
    key: "narrative",
    icon: ScrollText,
    kicker: "STEP 1 · THE STORY",
    title: "The Dungeon Master speaks.",
    body: (
      <>
        Every turn opens with a new passage in the panel above.
        Read carefully — <span className="gold-text">clues to survival</span> are woven
        into the prose. The AI Director rewrites the world around your choices.
      </>
    ),
    accent: "#7fb3ff",
  },
  {
    key: "actions",
    icon: Keyboard,
    kicker: "STEP 2 · YOUR MOVE",
    title: "Pick fast. Pick decisive.",
    body: (
      <>
        Four moves appear each turn. Tap them, or press{" "}
        <span className="gold-text font-heading">1 · 2 · 3 · 4</span> for instant
        response. Press <span className="gold-text font-heading">/</span> to write
        your own action — the Director will honor almost any intent. Press{" "}
        <span className="gold-text font-heading">?</span> to see every hotkey.
      </>
    ),
    accent: "#d4af37",
  },
  {
    key: "embers",
    icon: Flame,
    kicker: "STEP 3 · THE EMBER",
    title: "Three embers. Ten floors.",
    body: (
      <>
        You have <span className="gold-text">3 lives per day</span>. Die on floor 3
        or later and you'll drop souls the next hero can spend — this is a
        rogue-lite. Reach floor 10, slay the boss, and{" "}
        <span className="ember">the ember lights forever</span>.
      </>
    ),
    accent: "#e25822",
  },
];

export default function OnboardingOverlay({ open, onComplete }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Fire-and-forget event log — never blocks UI, never surfaces errors.
  const logEvent = (stepKey, action) => {
    api.post("/user/onboarding-step", { step: stepKey, action }).catch(() => {});
  };

  // Reset to step 0 whenever the overlay is (re-)opened
  useEffect(() => {
    if (open) {
      setStep(0);
      logEvent(STEPS[0].key, "viewed");
    }
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Allow arrow keys / Enter to advance so the tutorial itself teaches keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (["ArrowRight", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        advance();
      } else if (e.key === "Escape") {
        e.preventDefault();
        finish("skipped");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, step]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const advance = () => {
    logEvent(current.key, "advanced");
    if (isLast) return finish("completed");
    setStep((s) => {
      const nextIdx = Math.min(s + 1, STEPS.length - 1);
      logEvent(STEPS[nextIdx].key, "viewed");
      return nextIdx;
    });
  };

  const finish = async (reason = "skipped") => {
    if (busy) return;
    setBusy(true);
    // Log the terminal event so the funnel knows which step they left on.
    logEvent(current.key, reason);
    try {
      await api.post("/user/tutorial-complete", {});
    } catch { /* non-fatal — worst case they see it again next login */ }
    onComplete?.();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0"
      data-testid="onboarding-overlay"
      style={{
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        className="panel p-6 sm:p-8 max-w-lg w-full relative overflow-hidden"
        data-testid={`onboarding-step-${current.key}`}
        style={{
          background: "linear-gradient(180deg, rgba(20,12,6,0.98), rgba(6,4,2,0.98))",
          borderColor: current.accent,
          boxShadow: `0 0 40px ${current.accent}33`,
        }}
      >
        {/* Skip button */}
        <button
          onClick={() => finish("skipped")}
          disabled={busy}
          className="absolute top-3 right-3 flex items-center gap-1 text-[10px] tracking-[0.3em] ash-text hover:text-[#f0e6d2] transition-colors disabled:opacity-40"
          data-testid="onboarding-skip"
          aria-label="Skip tutorial"
        >
          SKIP <X size={12} />
        </button>

        {/* Step indicator dots */}
        <div className="flex items-center gap-2 mb-4">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className="h-1 rounded-full transition-all"
              style={{
                width: i === step ? "1.75rem" : "0.5rem",
                background: i === step ? current.accent : "rgba(139,121,101,0.35)",
              }}
              aria-label={`Step ${i + 1} of ${STEPS.length}`}
            />
          ))}
        </div>

        {/* Icon + kicker */}
        <div className="flex items-center gap-2" style={{ color: current.accent }}>
          <Icon size={16} />
          <span className="text-[10px] tracking-[0.4em]">{current.kicker}</span>
        </div>

        {/* Title */}
        <h2 className="font-heading text-2xl sm:text-3xl text-[#f0e6d2] mt-2">
          {current.title}
        </h2>

        {/* Body */}
        <p className="font-narrative text-base leading-relaxed text-[#c8b7a0] mt-3">
          {current.body}
        </p>

        {/* Advance button */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="text-[9px] tracking-[0.3em] ash-text hidden sm:flex items-center gap-1">
            <Sparkles size={10} /> ENTER OR → TO CONTINUE
          </div>
          <button
            onClick={advance}
            disabled={busy}
            data-testid={isLast ? "onboarding-finish-btn" : "onboarding-next-btn"}
            className="btn-primary flex items-center gap-2 ml-auto"
            style={{
              borderColor: current.accent,
              background: `linear-gradient(180deg, ${current.accent}22, rgba(6,4,2,0.9))`,
            }}
          >
            {isLast ? "BEGIN THE DESCENT" : "NEXT"}
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
